import { BACKEND_URL } from "@/lib/api";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";

export function PageHeader({ eyebrow, title, description, action }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-6 sm:mb-10 pb-6 border-b border-border/60">
      <div>
        {eyebrow && <div className="label-caps text-muted-foreground mb-2">{eyebrow}</div>}
        <h1 className="font-serif text-3xl sm:text-5xl tracking-tight leading-tight pb-1 text-brand-gradient">{title}</h1>
        {description && <p className="text-muted-foreground mt-3 text-sm max-w-xl">{description}</p>}
      </div>
      {action && <div className="flex flex-wrap gap-3 [&>div]:flex-wrap">{action}</div>}
    </div>
  );
}

const STATUS_STYLES = {
  scheduled: "bg-[hsl(40_30%_86%)] text-[hsl(30_35%_28%)]",
  in_progress: "bg-[hsl(30_10%_82%)] text-[hsl(20_10%_25%)]",
  completed: "bg-[hsl(120_18%_82%)] text-[hsl(140_25%_25%)]",
  cancelled: "bg-[hsl(10_35%_86%)] text-[hsl(8_45%_35%)]",
  paused: "bg-muted text-muted-foreground",
};

const STATUS_LABELS = {
  scheduled: "Scheduled",
  in_progress: "In Progress",
  completed: "Complete",
  cancelled: "Cancelled",
  paused: "Paused",
};

export function StatusBadge({ status }) {
  const label = STATUS_LABELS[status] || "Scheduled";
  return (
    <span
      className={`inline-block px-3 py-1 label-caps rounded-sm ${
        STATUS_STYLES[status] || STATUS_STYLES.scheduled
      }`}
    >
      {label}
    </span>
  );
}

export const money = (n) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(n || 0));


export function logoSrc(settings) {
  if (!settings) return null;
  if (settings.logo_path) return `${BACKEND_URL}/api/files/${settings.logo_path}`;
  return settings.logo_url || null;
}

export function fileSrc(path) {
  if (!path) return null;
  return `${BACKEND_URL}/api/files/${path}`;
}

export const TEAM_COLORS = [
  "#f21b7a", "#8a10f5", "#d81e5b", "#7c3aed", "#2563eb",
  "#0891b2", "#059669", "#f59e0b", "#ea580c", "#db2777",
];

export function EmptyState({ title, description, action }) {
  return (
    <div className="border border-dashed border-border/70 py-20 text-center rounded-sm">
      <p className="font-serif text-2xl text-foreground">{title}</p>
      {description && <p className="text-muted-foreground mt-2 text-sm">{description}</p>}
      {action && <div className="mt-6 flex justify-center">{action}</div>}
    </div>
  );
}

export function useUrlSearch() {
  const [sp] = useSearchParams();
  const [search, setSearch] = useState(sp.get("q") || "");
  useEffect(() => { setSearch(sp.get("q") || ""); }, [sp]);
  return [search, setSearch];
}

export function SearchInput({ value, onChange, placeholder = "Search…", testid = "page-search" }) {
  return (
    <div className="flex items-center gap-2 border border-border/70 rounded-sm px-3 py-2 bg-background focus-within:border-brand max-w-sm">
      <Search className="w-4 h-4 text-muted-foreground shrink-0" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="bg-transparent outline-none text-sm w-full placeholder:text-muted-foreground"
      />
    </div>
  );
}

export function matchText(term, ...fields) {
  const t = (term || "").trim().toLowerCase();
  if (!t) return true;
  return fields.filter(Boolean).join(" ").toLowerCase().includes(t);
}
